import React, { useEffect, useRef, useState } from "react";
import useEscapeLayer from "../useEscape";
import { confirmSpec, confirmWrites, readyToConfirm } from "./statusConfirm.mjs";
import DateField from "../datepicker/DateField";

/*
 * THE CONFIRM for every Change status… option (frame 1i, "Confirm · Remove from
 * pipeline", drawn once and reused for all of them, as the check asked).
 *
 * One grammar: the question as the title ("Remove Northwind from the pipeline?"),
 * the reason chips, the reminder chips or the day, the consequence in plain words
 * on the evidence grey (what changes, what closes, who stays), an optional note,
 * then Cancel and a BLACK Confirm: the yellow was spent on what opened it, and a
 * second yellow in a dialog would compete with it.
 *
 * What it asks for and what it says come from record/statusConfirm.mjs confirmSpec
 * (record/endings.mjs confirmFor for the endings, plus stage moves, Not now and the
 * vendor's note); the writes from confirmWrites, sent in order through
 * ops.runWrites. Not a lead goes through ops.runEnding instead, because it deletes
 * the person on screen and the shell closes the panel after it rather than
 * reloading a record that is gone.
 *
 * A reason that is not earned yet (Closed lost's "Declined after pricing" with no
 * pricing on record) is a greyed chip, and the line under the chips says why, so
 * the gate explains itself instead of looking broken. The error, when a write
 * fails, sits beside the Confirm that failed. Esc cancels, never mid-write, and
 * never while typing the note (escape.mjs).
 *
 * Props:
 *   record     the shell's bundle: company, contact, act, now
 *   ops        ops.runWrites(writes), ops.runEnding(id, choice) for not_a_lead
 *   endingId   "move:<stage>" | "won" | "hold" | "not_now" | "remove" | "lost" |
 *              "disqualify" | "vendor" | "not_a_lead" | "churn"
 *   preset     partial choice to start from: { remind: "6m" } from the last chase
 *              rung, { preset: "next_monday" } or { preset: "date" } from a hold chip
 *   onCancel   () => void
 *   onDone     () => void, after the writes succeeded
 *   spec, run  the Leads page's own confirm and writes (leadsStatus.mjs), for one
 *              row or a selection: the same card, its words and its sends supplied.
 *              spec.titleFor(choice), when given, retitles as the choice changes
 *              what a selection covers.
 */
export default function ConfirmCard({ record, ops, endingId, preset = {}, onCancel, onDone, spec: given = null, run = null }) {
  const { company, contact, act } = record;
  // The day the card opened: the sentences and the date limits must not move
  // under a reload that lands while it is open.
  const [now] = useState(() => new Date());
  const spec = given || confirmSpec(endingId, { company, contact, act, now });
  const [choice, setChoice] = useState(() => ({ ...((spec && spec.defaults) || {}), ...(preset || {}) }));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const ref = useRef(null);
  useEscapeLayer(true, () => !busy && onCancel && onCancel());

  // Focus goes into the dialog, so Tab starts inside it and a screen reader reads
  // the question first.
  useEffect(() => {
    if (ref.current && typeof ref.current.focus === "function") ref.current.focus({ preventScroll: true });
  }, []);

  if (!spec) return null;

  const set = (patch) => setChoice((c) => ({ ...c, ...patch }));
  const has = (f) => spec.fields.includes(f);
  const ready = readyToConfirm(spec, choice);
  const limits = spec.dateLimits || {};
  const title = spec.titleFor ? spec.titleFor(choice) : spec.title;

  const confirm = async () => {
    if (!ready || busy) return;
    setBusy(true);
    setError(null);
    try {
      if (run) await run(choice);
      else if (endingId === "not_a_lead") await ops.runEnding(endingId, choice);
      else await ops.runWrites(confirmWrites(endingId, choice, { company, contact, now: new Date() }));
      if (onDone) onDone();
    } catch (e) {
      setError((e && e.message) || "That didn’t save. Try again.");
      setBusy(false);
    }
  };

  const chips = (list, field, { disabledWhy = false } = {}) => (
    <div className="rec-chips">
      {list.map((r) => {
        const off = r.enabled === false;
        return (
          <button
            key={r.id}
            type="button"
            className={`rec-chip${choice[field] === r.id ? " is-on" : ""}`}
            aria-pressed={choice[field] === r.id}
            disabled={off || busy}
            title={disabledWhy && off && r.why ? r.why : undefined}
            onClick={() => set({ [field]: r.id })}
          >
            {r.label}
          </button>
        );
      })}
    </div>
  );

  const dateInput = (field, label, extra = {}) => (
    <DateField
      className="rec-cc__date"
      aria-label={label}
      value={choice[field] || ""}
      min={extra.min}
      max={extra.max}
      disabled={busy}
      onChange={(d) => set({ [field]: d })}
    />
  );

  return (
    <div className="rec-confirm rec-cc" role="dialog" aria-modal="true" aria-label={title} tabIndex={-1} ref={ref}>
      <div className="rec-confirm__title">{title}</div>

      {has("reason") && spec.reasons && (
        <div className="rec-confirm__field">
          <span className="rec-label">Why</span>
          {chips(spec.reasons, "reason", { disabledWhy: true })}
          {spec.unearned && <div className="rec-cc__why">{spec.unearned}</div>}
        </div>
      )}

      {has("remind") && spec.remind && (
        <div className="rec-confirm__field">
          <span className="rec-label">Remind me</span>
          {chips(spec.remind, "remind")}
          {choice.remind === "date" && dateInput("date", "Remind me on", { min: limits.min })}
        </div>
      )}

      {has("preset") && spec.presets && (
        <div className="rec-confirm__field">
          <span className="rec-label">Hold until</span>
          {chips(spec.presets, "preset")}
          {choice.preset === "date" && dateInput("date", "Hold until", { min: limits.min })}
        </div>
      )}

      {has("date") && !has("preset") && (
        <div className="rec-confirm__field">
          <span className="rec-label">Won on</span>
          {dateInput("date", "Won on", { max: limits.max })}
        </div>
      )}

      {has("notice") && (
        <div className="rec-confirm__field">
          <span className="rec-label">Notice received</span>
          {dateInput("notice", "Notice received", { max: limits.max })}
        </div>
      )}

      {has("reason_text") && (
        <div className="rec-confirm__field">
          <span className="rec-label">Why · optional</span>
          <input
            className="rec-input rec-cc__input"
            value={choice.reason_text || ""}
            placeholder="In a few words"
            aria-label="Why they left"
            disabled={busy}
            onChange={(e) => set({ reason_text: e.target.value })}
          />
        </div>
      )}

      <div className="rec-consequence">{spec.sentence(choice)}</div>

      {has("note") && (
        <div className="rec-confirm__field">
          <span className="rec-label">{spec.noteLabel}</span>
          <input
            className="rec-input rec-cc__input"
            value={choice.note || ""}
            placeholder="Add a note"
            aria-label={spec.noteLabel}
            disabled={busy}
            // No Enter-to-confirm: Not a lead deletes a person, and a stray Enter in
            // the note must never be the click that did it.
            onChange={(e) => set({ note: e.target.value })}
          />
        </div>
      )}

      <div className="rec-confirm__actions">
        {error && <span className="rec-err">{error}</span>}
        <button type="button" className="rec-btn rec-btn--plain rec-cc__btn" disabled={busy} onClick={onCancel}>
          Cancel
        </button>
        <button type="button" className="rec-btn rec-btn--dark rec-cc__btn" disabled={busy || !ready} onClick={confirm}>
          {busy ? "Saving…" : spec.confirmLabel || "Confirm"}
        </button>
      </div>
    </div>
  );
}
